/**
 * Channel-roster view for the in-channel screen.
 *
 * Renders one row per connection currently in the channel, keyed by
 * `connection_id`. The view is **scrollable**: with a 100-connection
 * server cap the list can be longer than the viewport. The PTT-relevant
 * "who is talking right now" info is rendered separately by
 * [`createTalkerList`] above this widget — that pinned section is
 * always visible regardless of how far down the user has scrolled.
 *
 * As with the talker list, adds and removes are idempotent — receiving
 * `member_joined` for an id already present, or `member_left` for an id
 * not present, is a no-op (per `PROTOCOL.md`'s set-keyed-by-id rule).
 *
 * No virtual DOM — at the v1 cap of ~100 entries direct DOM manipulation
 * is comfortably fast.
 */

import type { MemberInfo } from "../protocol";

/** Constructor options. */
export interface MemberListOptions {
    /** Container the list is appended into. */
    container: HTMLElement;
    /** Initial set of members, e.g. from `hello_ok.current_members`. */
    initial?: MemberInfo[];
    /**
     * Connection id of the local user. The matching row, if any, is
     * tagged with `(you)` so the user can locate themselves at a
     * glance. Optional — omit to leave the rendering symmetric.
     */
    selfConnectionId?: string;
}

/** Handle for mutating the list. */
export interface MemberListHandle {
    /** Add or update an entry. Idempotent. */
    add(member: MemberInfo): void;
    /** Remove the entry for `connection_id`. Idempotent. */
    remove(connectionId: string): void;
    /** Replace the whole set in one call (e.g. on reconnect). */
    setAll(members: MemberInfo[]): void;
    /**
     * Mark which member ids are currently holding a floor slot. Members
     * not in the set lose any "talking" highlight they had. Called by
     * the channel screen on every floor-state change so a member's
     * highlight follows them as they scroll into and out of view.
     */
    setTalkers(connectionIds: ReadonlySet<string>): void;
    /**
     * Update the connection id treated as "self". The previous
     * self-tag is cleared and the new id (if present in the list)
     * gets the tag. Called by the channel screen after a successful
     * reconnect, since the server hands out a fresh `connection_id`
     * each time the handshake completes.
     */
    updateSelf(connectionId: string | undefined): void;
    /** Detach all DOM nodes belonging to the list. */
    destroy(): void;
}

/**
 * Construct the member-list view inside `container`. Returns a handle
 * for incremental updates as `member_joined` / `member_left` events
 * arrive on the transport.
 */
export function createMemberList(opts: MemberListOptions): MemberListHandle {
    const root = document.createElement("div");
    root.className = "member-list";
    opts.container.appendChild(root);

    /** connection_id → row element. */
    const rows = new Map<string, HTMLElement>();
    /** Member ids currently holding a floor slot — drives the .talking class. */
    const talkers = new Set<string>();
    /** Mutable so the channel screen can refresh it after a reconnect. */
    let selfId: string | undefined = opts.selfConnectionId;

    const applyTalkingClass = (id: string, row: HTMLElement): void => {
        row.classList.toggle("talking", talkers.has(id));
    };

    const applySelfTag = (id: string, row: HTMLElement): void => {
        const existing = row.querySelector(".self-tag");
        if (id === selfId) {
            if (existing === null) {
                const tag = document.createElement("span");
                tag.className = "self-tag";
                tag.textContent = "(you)";
                row.appendChild(tag);
            }
        } else {
            existing?.remove();
        }
    };

    const addInternal = (m: MemberInfo): void => {
        if (rows.has(m.connection_id)) return;
        const row = document.createElement("div");
        row.className = "member";
        row.dataset.connectionId = m.connection_id;

        const dot = document.createElement("span");
        dot.className = "dot";
        dot.setAttribute("aria-hidden", "true");

        const name = document.createElement("span");
        name.className = "name";
        name.textContent = m.display_name;

        row.appendChild(dot);
        row.appendChild(name);

        applySelfTag(m.connection_id, row);
        applyTalkingClass(m.connection_id, row);
        root.appendChild(row);
        rows.set(m.connection_id, row);
    };

    const removeInternal = (connectionId: string): void => {
        const row = rows.get(connectionId);
        if (row === undefined) return;
        row.remove();
        rows.delete(connectionId);
    };

    for (const m of opts.initial ?? []) addInternal(m);

    return {
        add: addInternal,
        remove: removeInternal,
        setAll(members: MemberInfo[]): void {
            const wanted = new Set(members.map((m) => m.connection_id));
            for (const id of Array.from(rows.keys())) {
                if (!wanted.has(id)) removeInternal(id);
            }
            for (const m of members) addInternal(m);
        },
        setTalkers(connectionIds: ReadonlySet<string>): void {
            talkers.clear();
            for (const id of connectionIds) talkers.add(id);
            for (const [id, row] of rows) applyTalkingClass(id, row);
        },
        updateSelf(connectionId: string | undefined): void {
            selfId = connectionId;
            for (const [id, row] of rows) applySelfTag(id, row);
        },
        destroy(): void {
            root.remove();
            rows.clear();
            talkers.clear();
        },
    };
}
